/* GearFrost wishlist — localStorage-backed. Stores product HANDLES only and fetches fresh product
 * data when the drawer opens (no product-JSON dump). Custom elements:
 *   <wishlist-button data-product-handle>  heart toggle (product page + cards)
 *   <wishlist-count>                        header icon: count bubble + opens the drawer
 *   <wishlist-drawer>                       slide-out list (fetch fresh, remove, view)
 * All instances stay in sync via the 'gf:wishlist:change' document event. */
(function () {
  var KEY = 'gf_wishlist';
  var EVENT = 'gf:wishlist:change';

  function readList() {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  var Store = {
    get: function () {
      return readList();
    },
    save: function (list) {
      try {
        localStorage.setItem(KEY, JSON.stringify(list));
      } catch (e) {
        /* storage blocked — state lives only for this page view */
      }
      document.dispatchEvent(new CustomEvent(EVENT, { detail: { list: list } }));
    },
    has: function (handle) {
      return readList().indexOf(handle) !== -1;
    },
    toggle: function (handle) {
      var list = readList();
      var i = list.indexOf(handle);
      if (i === -1) list.push(handle);
      else list.splice(i, 1);
      this.save(list);
      return i === -1;
    },
    remove: function (handle) {
      this.save(
        readList().filter(function (h) {
          return h !== handle;
        })
      );
    },
  };
  window.GfWishlist = Store;

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Heart toggle */
  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class extends HTMLElement {
        connectedCallback() {
          this.handle = this.dataset.productHandle;
          this.button = this.querySelector('button') || this;
          this._onClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            Store.toggle(this.handle);
          };
          this._onChange = () => this.sync();
          this.button.addEventListener('click', this._onClick);
          document.addEventListener(EVENT, this._onChange);
          this.sync();
        }
        disconnectedCallback() {
          if (this.button) this.button.removeEventListener('click', this._onClick);
          document.removeEventListener(EVENT, this._onChange);
        }
        sync() {
          var active = Store.has(this.handle);
          this.classList.toggle('is-active', active);
          var target = this.button || this;
          target.setAttribute('aria-pressed', active ? 'true' : 'false');
          target.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
        }
      }
    );
  }

  /* Header icon: count bubble + drawer opener */
  if (!customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class extends HTMLElement {
        connectedCallback() {
          this.bubble = this.querySelector('[data-wishlist-bubble]');
          this.countEl = this.querySelector('[data-wishlist-count]');
          this.opener = this.querySelector('[data-wishlist-open]');
          this._onOpen = (e) => {
            e.preventDefault();
            var drawer = document.querySelector('wishlist-drawer');
            if (drawer) drawer.open();
          };
          this._onChange = () => this.render();
          if (this.opener) this.opener.addEventListener('click', this._onOpen);
          document.addEventListener(EVENT, this._onChange);
          this.render();
        }
        disconnectedCallback() {
          if (this.opener) this.opener.removeEventListener('click', this._onOpen);
          document.removeEventListener(EVENT, this._onChange);
        }
        render() {
          var n = Store.get().length;
          if (this.countEl) this.countEl.textContent = n;
          if (this.bubble) this.bubble.hidden = n === 0;
          // The number bubble is aria-hidden, so carry the count in the button's accessible name.
          if (this.opener) {
            this.opener.setAttribute('aria-label', n === 0 ? 'Wishlist' : 'Wishlist, ' + n + (n === 1 ? ' item' : ' items'));
          }
        }
      }
    );
  }

  /* Slide-out drawer */
  if (!customElements.get('wishlist-drawer')) {
    customElements.define(
      'wishlist-drawer',
      class extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-wishlist-list]');
          this.empty = this.querySelector('[data-wishlist-empty]');
          this.cache = {};

          var overlay = this.querySelector('[data-wishlist-overlay]');
          var closeBtn = this.querySelector('[data-wishlist-close]');
          if (overlay) overlay.addEventListener('click', () => this.close());
          if (closeBtn) closeBtn.addEventListener('click', () => this.close());
          this._onKey = (e) => {
            if (e.key === 'Escape') this.close();
          };
          if (this.list) {
            this.list.addEventListener('click', (e) => {
              var rm = e.target.closest('[data-wishlist-remove]');
              if (rm) Store.remove(rm.getAttribute('data-wishlist-remove'));
            });
          }
          this._onChange = () => {
            if (this.classList.contains('is-open')) this.renderItems();
          };
          document.addEventListener(EVENT, this._onChange);
        }
        disconnectedCallback() {
          document.removeEventListener(EVENT, this._onChange);
          document.removeEventListener('keydown', this._onKey);
        }
        open() {
          this._trigger = document.activeElement;
          this.classList.add('is-open');
          document.body.classList.add('gf-wl-open');
          document.addEventListener('keydown', this._onKey);
          this.renderItems();
          var panel = this.querySelector('.gf-wl-drawer__panel');
          var closeBtn = this.querySelector('[data-wishlist-close]');
          if (typeof trapFocus === 'function' && panel) trapFocus(panel, closeBtn);
          else if (closeBtn) closeBtn.focus();
        }
        close() {
          this.classList.remove('is-open');
          document.body.classList.remove('gf-wl-open');
          document.removeEventListener('keydown', this._onKey);
          if (typeof removeTrapFocus === 'function') removeTrapFocus(this._trigger);
          else if (this._trigger && this._trigger.focus) this._trigger.focus();
        }
        renderItems() {
          if (!this.list) return;
          var handles = Store.get();
          if (handles.length === 0) {
            this.list.innerHTML = '';
            if (this.empty) this.empty.hidden = false;
            return;
          }
          if (this.empty) this.empty.hidden = true;
          var need = handles.filter((h) => !(h in this.cache));
          Promise.all(
            need.map((h) =>
              fetch('/products/' + encodeURIComponent(h) + '.js', { headers: { Accept: 'application/json' } })
                .then((r) => {
                  if (r.ok) return r.json();
                  // 404 = product genuinely gone (drop it). Any other status (5xx / rate-limit) is
                  // transient — return undefined so we DON'T cache it and retry on the next open.
                  return r.status === 404 ? 'gone' : undefined;
                })
                .then((p) => {
                  if (p !== undefined) this.cache[h] = p;
                })
                .catch(() => {
                  /* network error — transient; leave uncached so it retries, never drop the saved item */
                })
            )
          ).then(() => {
            var rows = [];
            handles.forEach((h) => {
              var p = this.cache[h];
              if (p && p !== 'gone') rows.push(this.row(p));
              else if (p === 'gone') Store.remove(h); // confirmed 404 — product no longer exists
              // p === undefined (transient fetch failure): keep it saved, just skip rendering this round
            });
            this.list.innerHTML = rows.join('');
            // Items are injected asynchronously after open(), so the focus trap set in open() captured
            // only the close button. Re-arm it now that the list rows exist, preserving current focus.
            if (this.classList.contains('is-open') && typeof trapFocus === 'function') {
              var panel = this.querySelector('.gf-wl-drawer__panel');
              if (panel) {
                var keep = panel.contains(document.activeElement)
                  ? document.activeElement
                  : this.querySelector('[data-wishlist-close]');
                trapFocus(panel, keep);
              }
            }
          });
        }
        row(p) {
          var img = '';
          if (p.featured_image) {
            img = p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=200';
          }
          var title = escapeHtml(p.title);
          return (
            '<li class="gf-wl-item">' +
            '<a class="gf-wl-item__media" href="' + p.url + '" tabindex="-1" aria-hidden="true">' +
            (img ? '<img src="' + img + '" alt="" loading="lazy" width="72" height="72">' : '') +
            '</a>' +
            '<div class="gf-wl-item__info">' +
            '<a class="gf-wl-item__title" href="' + p.url + '">' + title + '</a>' +
            '<a class="gf-wl-item__view" href="' + p.url + '">View product</a>' +
            '</div>' +
            '<button type="button" class="gf-wl-item__remove" data-wishlist-remove="' +
            escapeHtml(p.handle) +
            '" aria-label="Remove ' + title + ' from wishlist">&times;</button>' +
            '</li>'
          );
        }
      }
    );
  }

  // Cross-tab sync: localStorage 'storage' events fire in OTHER tabs/windows. Re-broadcast our
  // document event so every wishlist instance (hearts + header count + open drawer) re-syncs.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) document.dispatchEvent(new CustomEvent(EVENT, { detail: { list: readList() } }));
  });
})();
